import React, { useEffect, useMemo, useState } from 'react'

import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    TextField,
    useTheme,
} from '@material-ui/core'
import { fade, makeStyles } from '@material-ui/core/styles'
import { Close, Search } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { Container } from '../../components'
import { useAppDispatch, useUser } from '../../hooks'
import { BaseUnitInspection, Folder, ModelMap, traverse } from '../../models'
import {
    assignUnitInspections,
    ASSIGN_UNIT_INSPECTION_REQUEST,
    RootState,
} from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    inspectionId: number
    root: Folder
    unitCompletionMap: ModelMap<BaseUnitInspection>
    onClose: () => void
    makeFilteredRequest: () => void
}

interface ReassignUnit {
    id: number
    name: string
    location: string
    areaId: number | null
    assigneeIds: number[]
    assigneeNames: string[]
}

type PersonIntent = 'add' | 'remove'

const useStyles = makeStyles((theme) => ({
    paper: {
        width: 'min(1080px, 94vw)',
        height: 'min(760px, 92vh)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 16,
    },
    search: {
        backgroundColor: theme.palette.common.white,
        borderRadius: 8,
    },
    locationHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.25, 2.5, 0.75),
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    unitCard: {
        display: 'flex',
        alignItems: 'center',
        margin: theme.spacing(1, 2, 0),
        padding: theme.spacing(0.75, 1, 0.75, 0.5),
        borderRadius: 10,
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.common.white,
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    unitCardSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: fade(theme.palette.primary.main, 0.08),
    },
    personRow: {
        display: 'flex',
        alignItems: 'center',
        margin: theme.spacing(0.5, 1.5),
        padding: theme.spacing(0.5, 1, 0.5, 0.25),
        borderRadius: 10,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.common.white,
        },
    },
    personRowSelected: {
        backgroundColor: theme.palette.common.white,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    chipStrip: {
        height: 32,
        minHeight: 32,
        margin: theme.spacing(1, 2, 0),
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        overflowX: 'auto',
        overflowY: 'hidden',
        flexShrink: 0,
    },
    chip: {
        flexShrink: 0,
    },
    avatar: {
        width: 32,
        height: 32,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
        fontWeight: 700,
        fontSize: 12,
        marginRight: theme.spacing(1.25),
        flexShrink: 0,
    },
}))

const initialsFor = (name: string) =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')

const assigneeIdsFor = (unitInspection: BaseUnitInspection | undefined) => {
    if (!unitInspection) return []
    if (
        unitInspection.assigned_users &&
        unitInspection.assigned_users.length > 0
    ) {
        return unitInspection.assigned_users
    }
    return unitInspection.assigned_to ? [unitInspection.assigned_to] : []
}

export const ReassignInspectionModal = (props: Props) => {
    const {
        open,
        inspectionId,
        root,
        unitCompletionMap,
        onClose,
        makeFilteredRequest,
    } = props
    const theme = useTheme()
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const { userList, actions } = useUser()
    const { getUserList } = actions
    const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>([])
    const [personIntent, setPersonIntent] = useState<
        Record<number, PersonIntent>
    >({})
    const [unitSearch, setUnitSearch] = useState('')
    const [personSearch, setPersonSearch] = useState('')

    const saving = useSelector(
        (state: RootState) =>
            state.inspection.isLoading[ASSIGN_UNIT_INSPECTION_REQUEST],
    )

    useEffect(() => {
        if (!open) return
        setSelectedUnitIds([])
        setPersonIntent({})
        setUnitSearch('')
        setPersonSearch('')
        getUserList({ params: { my_team: true } })
    }, [open])

    const userNameById = useMemo(() => {
        const names = new Map<number, string>()
        userList.forEach((user) => names.set(user.id, user.name))
        return names
    }, [userList])

    const units = useMemo(() => {
        const rows: ReassignUnit[] = []
        traverse(root, (folder) => {
            const location = `${folder.path}${folder.name}`
            folder.units.forEach((unit) => {
                const unitInspection = unitCompletionMap[unit.id]
                if (!unitInspection) return
                const assigneeIds = assigneeIdsFor(unitInspection)
                rows.push({
                    id: unit.id,
                    name: unit.name,
                    location,
                    areaId: unit.areas[0]?.id ?? null,
                    assigneeIds,
                    assigneeNames: assigneeIds
                        .map((id) => userNameById.get(id))
                        .filter((name): name is string => Boolean(name)),
                })
            })
        })
        rows.sort((a, b) => {
            const location = a.location.localeCompare(b.location)
            if (location !== 0) return location
            return a.name.localeCompare(b.name, undefined, { numeric: true })
        })
        return rows
    }, [root, unitCompletionMap, userNameById])

    const filteredUnits = useMemo(() => {
        const query = unitSearch.trim().toLowerCase()
        if (!query) return units
        return units.filter(
            (unit) =>
                unit.name.toLowerCase().includes(query) ||
                unit.location.toLowerCase().includes(query) ||
                unit.assigneeNames.some((name) =>
                    name.toLowerCase().includes(query),
                ),
        )
    }, [units, unitSearch])

    const filteredPeople = useMemo(() => {
        const query = personSearch.trim().toLowerCase()
        if (!query) return userList
        return userList.filter((user) =>
            user.name.toLowerCase().includes(query),
        )
    }, [userList, personSearch])

    const selectableUnits = filteredUnits.filter((unit) => unit.areaId !== null)
    const allVisibleSelected =
        selectableUnits.length > 0 &&
        selectableUnits.every((unit) => selectedUnitIds.includes(unit.id))

    const groupedUnits = useMemo(() => {
        const groups: { location: string; units: ReassignUnit[] }[] = []
        filteredUnits.forEach((unit) => {
            const current = groups[groups.length - 1]
            if (current && current.location === unit.location) {
                current.units.push(unit)
            } else {
                groups.push({ location: unit.location, units: [unit] })
            }
        })
        return groups
    }, [filteredUnits])

    const toggleUnit = (unitId: number) => {
        setSelectedUnitIds((current) =>
            current.includes(unitId)
                ? current.filter((id) => id !== unitId)
                : current.concat(unitId),
        )
    }

    const selectedUnits = units.filter((unit) =>
        selectedUnitIds.includes(unit.id),
    )

    const membershipFor = (userId: number) => {
        if (selectedUnits.length === 0) return 'none' as const
        const count = selectedUnits.filter((unit) =>
            unit.assigneeIds.includes(userId),
        ).length
        if (count === 0) return 'none' as const
        if (count === selectedUnits.length) return 'all' as const
        return 'some' as const
    }

    const personChecked = (userId: number) => {
        const intent = personIntent[userId]
        if (intent === 'add') return true
        if (intent === 'remove') return false
        return membershipFor(userId) === 'all'
    }

    const personIndeterminate = (userId: number) =>
        !personIntent[userId] && membershipFor(userId) === 'some'

    const togglePerson = (userId: number) => {
        const membership = membershipFor(userId)
        const intent = personIntent[userId]
        const checked = personChecked(userId)
        setPersonIntent((current) => {
            const next = { ...current }
            if (checked) {
                next[userId] = 'remove'
            } else if (!intent && membership === 'some') {
                next[userId] = 'add'
            } else if (intent === 'remove' && membership !== 'none') {
                delete next[userId]
            } else {
                next[userId] = 'add'
            }
            return next
        })
    }

    const peopleToAdd = userList.filter(
        (user) => personChecked(user.id) && membershipFor(user.id) !== 'all',
    )
    const peopleToRemove = userList.filter(
        (user) =>
            personIntent[user.id] === 'remove' &&
            membershipFor(user.id) !== 'none',
    )

    const toggleVisibleUnits = () => {
        const visibleIds = selectableUnits.map((unit) => unit.id)
        setSelectedUnitIds((current) => {
            if (allVisibleSelected) {
                return current.filter((id) => !visibleIds.includes(id))
            }
            return Array.from(new Set(current.concat(visibleIds)))
        })
    }

    const handleAddPeople = () => {
        const chosen = units.filter(
            (unit) => selectedUnitIds.includes(unit.id) && unit.areaId !== null,
        )
        if (chosen.length === 0) {
            toast.error('Select at least one unit')
            return
        }
        if (peopleToAdd.length === 0 && peopleToRemove.length === 0) {
            toast.error('Add or remove at least one person')
            return
        }
        dispatch(
            assignUnitInspections({
                body: {
                    inspection: inspectionId,
                    areas: chosen.map((unit) => unit.areaId as number),
                    assign_to:
                        peopleToAdd.length > 0
                            ? peopleToAdd.map((user) => user.id)
                            : undefined,
                    unassign_from:
                        peopleToRemove.length > 0
                            ? peopleToRemove.map((user) => user.id)
                            : undefined,
                },
            }),
        ).then(() => {
            toast.success('Assignments updated')
            makeFilteredRequest()
            onClose()
        })
    }

    return (
        <Modal
            open={open}
            onClose={saving ? undefined : onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper className={classes.paper}>
                <Container
                    alignItems="center"
                    style={{
                        padding: theme.spacing(2.5, 2, 2, 3),
                        borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                >
                    <Container direction="column">
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            Reassign
                        </span>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                                marginTop: 2,
                            }}
                        >
                            Check a person to add them. Uncheck someone who is
                            already assigned to remove them.
                        </span>
                    </Container>
                    <div style={{ flex: 1 }} />
                    <IconButton onClick={onClose} disabled={saving}>
                        <Close />
                    </IconButton>
                </Container>

                <Container
                    style={{
                        flex: 1,
                        minHeight: 0,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Container
                        direction="column"
                        style={{
                            flex: 1.5,
                            minWidth: 0,
                            minHeight: 0,
                            backgroundColor: theme.palette.common.white,
                            borderRight: `1px solid ${theme.palette.grey[200]}`,
                        }}
                    >
                        <Container
                            alignItems="center"
                            style={{
                                padding: theme.spacing(2, 2, 1),
                                gap: theme.spacing(1),
                            }}
                        >
                            <TextField
                                className={classes.search}
                                value={unitSearch}
                                onChange={(event) =>
                                    setUnitSearch(event.target.value)
                                }
                                placeholder="Search units or people"
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="disabled" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                onClick={toggleVisibleUnits}
                                disabled={selectableUnits.length === 0}
                                style={{
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {allVisibleSelected ? 'Clear' : 'Select all'}
                            </Button>
                        </Container>
                        <div
                            style={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                paddingBottom: theme.spacing(1.5),
                            }}
                        >
                            {units.length === 0 && (
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        display: 'block',
                                        padding: theme.spacing(2, 3),
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    No units are on this inspection yet.
                                </span>
                            )}
                            {units.length > 0 && filteredUnits.length === 0 && (
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        display: 'block',
                                        padding: theme.spacing(2, 3),
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    No units match that search.
                                </span>
                            )}
                            {groupedUnits.map((group) => (
                                <div key={group.location}>
                                    <div className={classes.locationHeader}>
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.body2,
                                                ),
                                                fontWeight: 700,
                                            }}
                                        >
                                            {group.location || 'Units'}
                                        </span>
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.caption,
                                                ),
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            {group.units.length}
                                        </span>
                                    </div>
                                    {group.units.map((unit) => {
                                        const checked = selectedUnitIds.includes(
                                            unit.id,
                                        )
                                        const shownNames = unit.assigneeNames.slice(
                                            0,
                                            3,
                                        )
                                        const extraNames =
                                            unit.assigneeNames.length -
                                            shownNames.length
                                        return (
                                            <div
                                                key={unit.id}
                                                className={`${
                                                    classes.unitCard
                                                } ${
                                                    checked
                                                        ? classes.unitCardSelected
                                                        : ''
                                                }`}
                                                onClick={() => {
                                                    if (unit.areaId !== null) {
                                                        toggleUnit(unit.id)
                                                    }
                                                }}
                                            >
                                                <Checkbox
                                                    color="primary"
                                                    checked={checked}
                                                    disabled={
                                                        unit.areaId === null
                                                    }
                                                    onChange={() =>
                                                        toggleUnit(unit.id)
                                                    }
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                />
                                                <Container
                                                    direction="column"
                                                    style={{
                                                        minWidth: 0,
                                                        flex: 1,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            ...typographyStyle(
                                                                theme.typography
                                                                    .body1,
                                                            ),
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {unit.name}
                                                    </span>
                                                    <Container
                                                        flexWrap="wrap"
                                                        style={{
                                                            marginTop: 4,
                                                            gap: 4,
                                                        }}
                                                    >
                                                        {shownNames.length ===
                                                            0 && (
                                                            <Chip
                                                                size="small"
                                                                label="My Team"
                                                                style={{
                                                                    height: 22,
                                                                }}
                                                            />
                                                        )}
                                                        {shownNames.map(
                                                            (name) => (
                                                                <Chip
                                                                    key={name}
                                                                    size="small"
                                                                    label={name}
                                                                    style={{
                                                                        height: 22,
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                        {extraNames > 0 && (
                                                            <Chip
                                                                size="small"
                                                                label={`+${extraNames}`}
                                                                style={{
                                                                    height: 22,
                                                                }}
                                                            />
                                                        )}
                                                    </Container>
                                                </Container>
                                            </div>
                                        )
                                    })}
                                </div>
                            ))}
                        </div>
                    </Container>

                    <Container
                        direction="column"
                        style={{
                            width: 340,
                            minWidth: 280,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Container
                            direction="column"
                            style={{
                                padding: theme.spacing(2, 2, 1),
                                flexShrink: 0,
                            }}
                        >
                            <Container alignItems="center">
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body1,
                                        ),
                                        fontWeight: 700,
                                    }}
                                >
                                    Assignees
                                </span>
                                <div style={{ flex: 1 }} />
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.caption,
                                        ),
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {peopleToAdd.length} add
                                    {' · '}
                                    {peopleToRemove.length} remove
                                </span>
                            </Container>
                            <TextField
                                className={classes.search}
                                value={personSearch}
                                onChange={(event) =>
                                    setPersonSearch(event.target.value)
                                }
                                placeholder="Search people"
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{ marginTop: theme.spacing(1.5) }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="disabled" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Container>
                        <div className={classes.chipStrip}>
                            {peopleToAdd.map((user) => (
                                <Chip
                                    key={`add-${user.id}`}
                                    className={classes.chip}
                                    size="small"
                                    color="primary"
                                    label={user.name}
                                    onDelete={() => togglePerson(user.id)}
                                />
                            ))}
                            {peopleToRemove.map((user) => (
                                <Chip
                                    key={`remove-${user.id}`}
                                    className={classes.chip}
                                    size="small"
                                    variant="outlined"
                                    label={`Remove ${user.name}`}
                                    onDelete={() => togglePerson(user.id)}
                                />
                            ))}
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                paddingBottom: theme.spacing(1),
                            }}
                        >
                            {filteredPeople.length === 0 && (
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        display: 'block',
                                        padding: theme.spacing(1, 3),
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    No people match that search.
                                </span>
                            )}
                            {filteredPeople.map((user) => {
                                const checked = personChecked(user.id)
                                return (
                                    <div
                                        key={user.id}
                                        className={`${classes.personRow} ${
                                            checked
                                                ? classes.personRowSelected
                                                : ''
                                        }`}
                                        onClick={() => togglePerson(user.id)}
                                    >
                                        <Checkbox
                                            color="primary"
                                            checked={checked}
                                            indeterminate={personIndeterminate(
                                                user.id,
                                            )}
                                            onChange={() =>
                                                togglePerson(user.id)
                                            }
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        />
                                        <div
                                            className={classes.avatar}
                                            style={{
                                                backgroundColor:
                                                    user.profile_color ||
                                                    theme.palette.primary.main,
                                            }}
                                        >
                                            {initialsFor(user.name)}
                                        </div>
                                        <span
                                            style={typographyStyle(
                                                theme.typography.body1,
                                            )}
                                        >
                                            {user.name}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>
                    </Container>
                </Container>

                <Container
                    alignItems="center"
                    style={{
                        padding: theme.spacing(1.5, 2.5),
                        borderTop: `1px solid ${theme.palette.grey[200]}`,
                        backgroundColor: theme.palette.common.white,
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.secondary,
                            backgroundColor: theme.palette.grey[100],
                            borderRadius: 999,
                            padding: theme.spacing(0.5, 1.5),
                        }}
                    >
                        {selectedUnitIds.length}{' '}
                        {selectedUnitIds.length === 1 ? 'unit' : 'units'}
                        {' · '}
                        {peopleToAdd.length} add
                        {' · '}
                        {peopleToRemove.length} remove
                    </span>
                    <div style={{ flex: 1 }} />
                    {saving ? (
                        <CircularProgress size={22} />
                    ) : (
                        <>
                            <Button
                                onClick={onClose}
                                style={{
                                    marginRight: theme.spacing(1),
                                    textTransform: 'none',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddPeople}
                                disabled={
                                    selectedUnitIds.length === 0 ||
                                    (peopleToAdd.length === 0 &&
                                        peopleToRemove.length === 0)
                                }
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    padding: theme.spacing(0.75, 2.5),
                                    boxShadow: 'none',
                                }}
                            >
                                Save
                            </Button>
                        </>
                    )}
                </Container>
            </Paper>
        </Modal>
    )
}
